// src/lib/motionSafe.js
import React from "react";
import {
  motion as fmMotion,
  AnimatePresence,
  MotionConfig,
} from "framer-motion";
import { useVisualMode } from "../utils/visualMode";

/**
 * Käärii Framerin motion-komponentin niin,
 * että minimal-tilassa se piirtää ilman animaatio-proppeja.
 */
function makeGuardedMotion(Comp) {
  const Guarded = React.forwardRef(function GuardedMotion(props, ref) {
    const { isMinimal } = useVisualMode();

    // Täysi bilemoodi → käytä Frameria sellaisenaan
    if (!isMinimal) {
      return <Comp ref={ref} {...props} />;
    }

    // Minimal-moodi → poistetaan raskaammat animaatiot,
    // mutta PIDETÄÄN sisältö näkyvissä ja animate-kohdearvot käytössä.
    const {
      whileHover,
      whileTap,
      whileFocus,
      whileInView,
      drag,
      layout,
      transition,
      ...rest
    } = props;

    return (
      <Comp
        ref={ref}
        {...rest}                     // sisältää mm. animate, initial, exit jos annettu
        initial={false}              // ei aloiteta "piilosta"
        exit={undefined}             // ei exit-animaatiota (AnimatePresence ei hävitä näkymättömäksi)
        transition={{ duration: 0 }} // kaikki muutokset instant
        style={{ ...props.style, opacity: 1, visibility: "visible" }}
      />
    );
  });

  Guarded.displayName =
    `GuardedMotion(${Comp.displayName || Comp.name || "Component"})`;

  return Guarded;
}

// ✅ Kattaa yleisimmät motion-elementit
export const motion = {
  // layout-kontit
  div: makeGuardedMotion(fmMotion.div),
  section: makeGuardedMotion(fmMotion.section),
  main: makeGuardedMotion(fmMotion.main),
  header: makeGuardedMotion(fmMotion.header),
  footer: makeGuardedMotion(fmMotion.footer),
  nav: makeGuardedMotion(fmMotion.nav),
  article: makeGuardedMotion(fmMotion.article),
  aside: makeGuardedMotion(fmMotion.aside),

  // typografia
  span: makeGuardedMotion(fmMotion.span),
  p: makeGuardedMotion(fmMotion.p),
  h1: makeGuardedMotion(fmMotion.h1),
  h2: makeGuardedMotion(fmMotion.h2),
  h3: makeGuardedMotion(fmMotion.h3),
  h4: makeGuardedMotion(fmMotion.h4),

  // listat
  ul: makeGuardedMotion(fmMotion.ul),
  li: makeGuardedMotion(fmMotion.li),

  // media + interaktio
  img: makeGuardedMotion(fmMotion.img),
  a: makeGuardedMotion(fmMotion.a),
  button: makeGuardedMotion(fmMotion.button),
};

// Viedään nämä eteenpäin, jos käytät niitä jossain:
export { AnimatePresence, MotionConfig };
